import { signInWithEmailAndPassword } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import FullPageLayout from "../../components/FullPageLayout/FullPageLayout";
import { auth } from "../../firebase";
import { useAppAuthState } from "../../hooks/use-app-auth-state";
import { getOrCreateUser } from "../../services/userService";
import { SitePage } from "../../types/SitePage";
import "./LoginPage.scss";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { unverifiedFireUser, isLoggedIn, isAuthLoading } = useAppAuthState();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isAuthLoading && isLoggedIn) {
      // If user is logged in and verified, redirect to app home
      navigate("/home");
    } else if (
      !isAuthLoading &&
      unverifiedFireUser &&
      !unverifiedFireUser.emailVerified
    ) {
      // If user exists but not verified, redirect to verify email page
      navigate("/verify-email");
    }
  }, [unverifiedFireUser, isLoggedIn, isAuthLoading, navigate]);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      // The useEffect will handle redirection based on emailVerified status
      if (!userCredential.user.emailVerified) {
        navigate("/verify-email");
      } else {
        await getOrCreateUser(userCredential.user);
        navigate("/home");
      }
    } catch (err: any) {
      if (
        err.code === "auth/invalid-credential" ||
        err.code === "auth/user-not-found" ||
        err.code === "auth/wrong-password" ||
        err.code === "auth/invalid-email"
      ) {
        setError("Invalid email or password.");
      } else {
        setError("Failed to log in. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuthLoading) {
    return (
      <FullPageLayout page={SitePage.Login} title="Login">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </FullPageLayout>
    );
  }

  return (
    <FullPageLayout page={SitePage.Login} title="Login">
      <h2>Login</h2>
      <Form onSubmit={handleLogin} className="login-form">
        {error && <Alert variant="info">{error}</Alert>}
        <Form.Group className="mb-3" controlId="formBasicEmail">
          <Form.Label>Email address</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="formBasicPassword">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Form.Group>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              Logging in...
            </>
          ) : (
            "Login"
          )}
        </Button>
        <div className="register-cta">
          Don't have an account? <Link to="/register">Sign up</Link>
        </div>
      </Form>
    </FullPageLayout>
  );
};

export default LoginPage;
